import type { DisabledReason, PlannedInput, StepPlan } from "../../../../api/content/stepPlans";

/**
 * What the current step asks and what it folds away (F56), from module B's plan (ADR-0032
 * D7): the inputs a person still has to give are its question, and so is the entry when the
 * Thread works on none ("Which entry?"); everything the plan fills, from an earlier step, a
 * default or the Thread, folds under "Filled automatically". Pure, so the split is tested in
 * one place; the plan decides, this only sorts.
 */
export interface StepInputs {
  /** The inputs the plan says a person gives (`asksPerson`): asked, in the plan's order. */
  readonly asked: readonly PlannedInput[];
  /** The input the plan says a person picks the entry for (`asksEntry`): asked as "Which entry?". */
  readonly entry: PlannedInput | null;
  /** Inputs with a value or a source, and optional ones nothing fills: folded. */
  readonly folded: readonly PlannedInput[];
  /** How many of the folded ones, the prompt included, have a value. */
  readonly filledCount: number;
  /** Why else it cannot run, such as a value that does not fit: said in one line. */
  readonly otherReasons: readonly DisabledReason[];
}

/**
 * `base` sorts: the plan before this attempt's values, so an input stays where the person
 * answers it while they type. `current` gives the values: the plan for the values given.
 */
export function stepInputs(base: StepPlan, current: StepPlan): StepInputs {
  const isAsked = (input: PlannedInput) => input.asksPerson;
  const isEntry = (input: PlannedInput) => input.asksEntry;
  const askedNames = new Set(base.inputs.filter(isAsked).map((input) => input.name));
  const entryNames = new Set(base.inputs.filter(isEntry).map((input) => input.name));
  const asked = current.inputs.filter((input) => askedNames.has(input.name));
  const folded = current.inputs.filter((input) => !askedNames.has(input.name) && !entryNames.has(input.name));
  const answered = (reason: DisabledReason) =>
    (reason.code === "missing_input" && reason.input !== null && askedNames.has(reason.input)) || (reason.code === "missing_context" && reason.input !== null && entryNames.has(reason.input));
  return {
    asked,
    entry: current.inputs.find((input) => entryNames.has(input.name)) ?? null,
    folded,
    filledCount: folded.filter((input) => input.source !== "missing").length + (current.prompt === null ? 0 : 1),
    otherReasons: current.reasons.filter((reason) => !answered(reason)),
  };
}
